import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import routes from 'library/Routes';
import { compose } from 'recompose';
import { withLocale } from '../../../hocs/withLocale';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import { GELIOS } from 'constants/whileLabel';
import { StateProps } from '../../../types';
import { RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';

export const HOME_META_DESCRIPTION =
  'See how Voopty business management software serves educators, tutors, kids clubs, schools, and dance studios, and helps people find and book classes.';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: '0 auto',
      maxWidth: 1360,
      height: '100%',
      width: '100%',
    },
    split: {
      minHeight: '93vh',
    },
    side: {
      padding: theme.spacing(5),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundSize: 'cover',
      position: 'relative',
      '&:after': {
        content: '" "',
        position: 'absolute',
        top: '0',
        bottom: '0',
        right: '0',
        left: '0',
        zIndex: '0',
      },
    },
    sideText: {
      position: 'relative',
      zIndex: '1',
    },
    business: {
      color: theme.palette.primary.light,
      '&:after': {
        backgroundColor: 'rgba(241, 150, 68, 0.7);',
      },
      '&:hover:after': {
        backgroundColor: 'rgba(241, 150, 68, 0.95);',
      },
    },
    student: {
      '&:after': {
        backgroundColor: 'rgba(255, 255, 255, 0.7);',
      },
      '&:hover:after': {
        backgroundColor: 'rgba(255, 255, 255, 0.95);',
      },
    },
    outlineLight: {
      borderWidth: '3px',
      borderRadius: '0',
      color: theme.palette.primary.light,
      borderColor: theme.palette.primary.light,

      '&:hover': {
        color: theme.palette.primary.light,
      },
    },
    outlineDark: {
      borderWidth: '3px',
      borderRadius: '0',
      color: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,

      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
  });

interface HomeViewProps extends StateProps, WithStyles<typeof styles>, RouteComponentProps {
  domain: {
    staticUrl: string;
    owner?: {
      organizationSettings?: {
        clientCabinet?: string;
      };
    };
  };
}

export class HomeView extends React.Component<
  HomeViewProps,
  { triedFirst: boolean; triedSecond: boolean; src: string }
> {
  state = {
    triedFirst: false,
    triedSecond: false,
    src: 'https://landing.voopty.com.ua/',
  };

  componentDidMount(): void {
    const clientCabinet = this.props?.domain?.owner?.organizationSettings?.clientCabinet;
    if (clientCabinet === GELIOS) {
      this.props.history.push(routes.studioClientClassesGelios());
    }
  }

  handleIframeLoad = () => {
    // If loaded, do nothing (success)
  };

  handleIframeError = () => {
    // This event is unreliable for cross-origin, so we use a timeout fallback as well
    if (!this.state.triedFirst) {
      this.setState({
        src: 'https://voopty.com.ua/',
        triedFirst: true,
      });
    } else if (!this.state.triedSecond) {
      this.setState({ triedSecond: true }, () => {
        window.location.replace('https://biz.voopty.com');
      });
    }
  };

  handleIframeTimeout = () => {
    // If iframe hasn't loaded in 3 seconds, try next or redirect
    if (!this.state.triedFirst) {
      this.setState({
        src: 'https://voopty.com.ua/',
        triedFirst: true,
      });
    } else if (!this.state.triedSecond) {
      this.setState({ triedSecond: true }, () => {
        window.location.replace('https://biz.voopty.com');
      });
    }
  };

  componentDidUpdate(prevProps: HomeViewProps, prevState: any) {
    if (prevState.src !== this.state.src) {
      // Set a timeout to detect load failure
      this.iframeTimeout = setTimeout(this.handleIframeTimeout, 3000);
    }
  }

  componentWillUnmount() {
    if (this.iframeTimeout) {
      clearTimeout(this.iframeTimeout);
    }
  }

  iframeTimeout: any;

  render = (): React.ReactNode => {
    const { classes, translate, currentLanguage, domain } = this.props;
    return (
      <React.Fragment>
        <Helmet>
          <meta name="description" content={HOME_META_DESCRIPTION} />
          <meta property="og:description" content={HOME_META_DESCRIPTION} />
          <meta name="twitter:description" content={HOME_META_DESCRIPTION} />
        </Helmet>
        <iframe
          src={this.state.src}
          style={{ width: '100%', height: '100vh', border: 'none' }}
          onLoad={() => {
            if (this.iframeTimeout) clearTimeout(this.iframeTimeout);
            this.handleIframeLoad();
          }}
          onError={this.handleIframeError}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
        ></iframe>
      </React.Fragment>
    );
  };
}

export default compose(
  connect((state: any) => ({ user: state.user, domain: state.domain })),
  withLocale,
  withStyles(styles)
)(HomeView);
